import type { RequestOptions } from '@/shared/api';
import type {
  AuthResponse,
  CreatePredictionPayload,
  DerbyState,
  FuncoinBalance,
  Leaderboard,
  MatchCard,
  Prediction,
  PredictionPopup,
  Achievement,
  ClaimResult,
  CurrentDraws,
  DailyActivity,
  PendingResult,
  FreebetsStatus,
  MonthlyDraw,
  RewardTrack,
  UserProfile,
  SeasonFinalDraw,
  UserSummary,
} from '@/shared/api';

/**
 * Заглушка бэкенда для вёрстки на dev-площадке.
 *
 * Формы ответов берутся из `@betboom/api-contract`, поэтому расхождение
 * с настоящим API ломает типизацию здесь же, а не проявляется кривым
 * экраном.
 *
 * Матчи открытой ленты переключаются параметром `?scenario=` в адресе, без
 * пересборки: `default` — открытый матч, ничего не поставлено; `empty` —
 * пустая лента (с промо Дерби, оно в демо всегда открыто); `progress` —
 * исход уже поставлен, карточка «В процессе». Список — SCENARIOS.
 * Вкладка «Завершённые» (GET /matches) не сценарная — всегда один пример
 * на «Успех» и один на «Провал», FINISHED_MATCHES.
 *
 * Работает только при `env.demo` (dev + VITE_FE_DEMO=true) — в прод-сборке
 * ветка выпадает из бандла вместе с флагом.
 */

const USER: UserProfile = {
  id: '42',
  telegramId: '551200341',
  displayName: 'Демо-игрок',
  username: 'demo_player',
  firstName: 'Демо',
  isSubscribed: true,
  hasPhone: false,
  referralCode: 'DEMO1234',
  referralStartParam: 'ref_DEMO1234',
  referralLink:
    'https://t.me/betboom_champ_bot/betboom_champ?startapp=ref_DEMO1234',
  channelUrl: 'https://t.me/betboom',
  acquisitionSource: null,
};

/**
 * Профиль вместе со счётчиками: их показывает шапка на каждом экране.
 * Типизировано `UserSummary`, чтобы расхождение с контрактом падало
 * на `tsc`, а не кривым числом на экране.
 */
const USER_SUMMARY: UserSummary = {
  profile: USER,
  funcoinBalance: 180,
  weeklyPoints: 4200,
  monthlyPoints: 12_800,
  freebetsEarned: 3,
  hasActivePeriod: true,
};

/** Ачивки по редакции ГДД §9.4: многоусловная и с целью выше потолка сегментов. */
const ACHIEVEMENTS: Achievement[] = [
  {
    id: 6,
    code: 'okko',
    title: 'Окко',
    description: 'Подписка на телеграм-канал Okko Спорт',
    rewardPoints: 500,
    rewardFreebets: 0,
    status: 'IN_PROGRESS',
    countsForSeasonFinal: false,
    isLegendary: true,
    // ponytail: сервер такого условия (подписка на ЧУЖОЙ канал) пока
    // не знает вовсе — переиспользуем CHANNEL_SUBSCRIPTION ради превью
    // кнопки «Подписаться» (см. AchievementCard.tsx).
    conditions: [
      {
        type: 'CHANNEL_SUBSCRIPTION',
        label: 'Подписка на канал',
        targetValue: 1,
        currentValue: 0,
        isCompleted: false,
      },
    ],
  },
  {
    id: 1,
    code: 'fanat',
    title: 'Фанат',
    description: 'Подпишись на Telegram-канал BETBOOM',
    rewardPoints: 500,
    rewardFreebets: 0,
    status: 'COMPLETED',
    countsForSeasonFinal: true,
    isLegendary: false,
    conditions: [
      {
        type: 'CHANNEL_SUBSCRIPTION',
        label: 'Подписка на канал',
        targetValue: 1,
        currentValue: 1,
        isCompleted: true,
      },
    ],
  },
  {
    id: 2,
    code: 'kaper',
    title: 'Каппер',
    description: 'Сделай 20 успешных Прогнозов',
    rewardPoints: 1500,
    rewardFreebets: 0,
    status: 'IN_PROGRESS',
    countsForSeasonFinal: false,
    isLegendary: false,
    conditions: [
      {
        type: 'SUCCESSFUL_PREDICTIONS',
        label: 'Успешные Прогнозы',
        targetValue: 20,
        currentValue: 7,
        isCompleted: false,
      },
    ],
  },
  {
    id: 3,
    code: 'community-pillar',
    title: 'Столп сообщества',
    description: 'Комментируй, реагируй и приводи друзей',
    rewardPoints: 2000,
    rewardFreebets: 0,
    status: 'IN_PROGRESS',
    countsForSeasonFinal: false,
    isLegendary: false,
    conditions: [
      {
        type: 'CHANNEL_COMMENTS',
        label: 'Комментарии',
        targetValue: 5,
        currentValue: 2,
        isCompleted: false,
      },
      {
        type: 'REACTIONS_GIVEN',
        label: 'Реакции',
        targetValue: 5,
        currentValue: 5,
        isCompleted: true,
      },
      {
        type: 'REFERRALS',
        label: 'Друзья',
        targetValue: 1,
        currentValue: 0,
        isCompleted: false,
      },
    ],
  },
  {
    id: 4,
    code: 'marathoner',
    title: 'Марафонец',
    description: 'Набери 10 000 Очков за сезон',
    rewardPoints: 5000,
    rewardFreebets: 0,
    status: 'IN_PROGRESS',
    countsForSeasonFinal: true,
    isLegendary: true,
    conditions: [
      {
        type: 'POINTS_TOTAL',
        label: 'Очки',
        targetValue: 10_000,
        currentValue: 5000,
        isCompleted: false,
      },
    ],
  },
  {
    id: 5,
    code: 'celebrity',
    title: 'Селеба',
    description: 'Получи 50 реакций на свои сообщения в чате BETBOOM',
    rewardPoints: 800,
    rewardFreebets: 0,
    status: 'IN_PROGRESS',
    countsForSeasonFinal: false,
    isLegendary: false,
    // Пятая карточка — специально для превью «Смотреть ещё»/«Скрыть»:
    // до неё в списке ровно четыре, столько же видно в свёрнутом виде.
    conditions: [
      {
        type: 'REACTIONS_RECEIVED',
        label: 'Реакции',
        targetValue: 50,
        currentValue: 12,
        isCompleted: false,
      },
    ],
  },
  {
    // Награда Фрибетами сверх Очков — макет 1000:7620 «Меценат»
    id: 7,
    code: 'booster_3',
    title: 'Меценат',
    description: 'Забустить канал BETBOOM три раза',
    rewardPoints: 450,
    rewardFreebets: 400,
    status: 'IN_PROGRESS',
    countsForSeasonFinal: false,
    isLegendary: true,
    conditions: [
      {
        type: 'CHANNEL_BOOSTS',
        label: 'Бусты',
        targetValue: 3,
        currentValue: 1,
        isCompleted: false,
      },
    ],
  },
];

/** Проведённый Месячный розыгрыш — из него строится блок «Призы месяца». */
const MONTHLY_DRAW: MonthlyDraw = {
  cycleIndex: 1,
  status: 'COMPLETED',
  conductedAt: '2030-02-08T09:00:00.000Z',
  myRank: 7,
  pools: [
    {
      type: 'TOP_10',
      rankFrom: 1,
      rankTo: 10,
      winnersCount: 1,
      actualWinnersCount: 1,
      prize: {
        code: 'station',
        title: 'PS5 SLIM',
        description: null,
        kind: 'PHYSICAL_WITH_FREEBETS',
        freebetAmount: 5000,
        imageUrl: null,
      },
      isParticipant: true,
      isEligible: true,
      exclusionReason: null,
    },
    {
      type: 'TOP_100',
      rankFrom: 1,
      rankTo: 100,
      winnersCount: 15,
      actualWinnersCount: 15,
      prize: {
        code: 'fb-2000',
        title: 'Фрибеты',
        description: null,
        kind: 'FREEBETS',
        freebetAmount: 2000,
        imageUrl: null,
      },
      isParticipant: true,
      isEligible: true,
      exclusionReason: null,
    },
  ],
  myPrize: null,
};

/** `null` — Финальный розыгрыш не подготовлен: так и будет до июня. */
const SEASON_FINAL: SeasonFinalDraw | null = null;

/** Анонс призов текущего цикла — работает с первого дня, до жеребьёвки. */
const CURRENT_DRAWS: CurrentDraws = {
  cycleIndex: 1,
  cycleTitle: 'Месячный цикл 1',
  cycleStartsAt: '2026-09-07T21:00:00.000Z',
  cycleEndsAt: '2026-10-07T21:00:00.000Z',
  pools: [
    {
      type: 'TOP_10',
      rankFrom: 1,
      rankTo: 10,
      winnersCount: 1,
      prize: {
        code: 'top10_month_1',
        title: 'PS5 SLIM',
        description: null,
        kind: 'PHYSICAL_WITH_FREEBETS',
        freebetAmount: 5000,
        imageUrl: null,
      },
    },
    {
      type: 'TOP_100',
      rankFrom: 1,
      rankTo: 100,
      winnersCount: 15,
      prize: {
        code: 'fb-2000',
        title: 'Фрибеты',
        description: null,
        kind: 'FREEBETS',
        freebetAmount: 2000,
        imageUrl: null,
      },
    },
  ],
  seasonPrize: null,
  seasonWinningClub: null,
  seasonTripCity: null,
};

/** Заявка подтверждена букмекером — кнопка «Получить» видна. */
const FREEBETS: FreebetsStatus = {
  earnedThisCycle: 3,
  payoutStatus: 'CONFIRMED',
  payoutAmount: 3,
  hasPhone: true,
  isSubscribed: true,
  canClaim: true,
  claimDeadlineAt: '2030-02-15T20:59:59.000Z',
};

const CLAIM: ClaimResult = {
  deeplink: 'https://betboom.ru/promo/freebets?token=demo',
};

/** «Сегодня доступно»: лимит комментариев выбран, реакции только начаты. */
const DAILY_ACTIVITY: DailyActivity = {
  hasActivePeriod: true,
  gameDayLabel: '2026-09-14',
  predictions: { made: 1, available: 3 },
  items: [
    {
      type: 'CHANNEL_COMMENT',
      awardedToday: 10,
      dailyLimit: 10,
      pointsPerAction: 4,
    },
    {
      type: 'CHAT_MESSAGE',
      awardedToday: 2,
      dailyLimit: 10,
      pointsPerAction: 4,
    },
    {
      type: 'REACTION_GIVEN',
      awardedToday: 1,
      dailyLimit: 10,
      pointsPerAction: 2,
    },
    {
      type: 'REACTION_RECEIVED',
      awardedToday: 7,
      dailyLimit: 10,
      pointsPerAction: 2,
    },
    { type: 'REFERRAL', awardedToday: 0, dailyLimit: 2, pointsPerAction: 20 },
  ],
  chatUrl: 'https://t.me/betboom_chat',
};

const MATCH_17: MatchCard = {
  id: '17',
  competition: { id: 1, name: 'Лига чемпионов' },
  homeTeam: { id: 10, name: 'Реал Мадрид', shortName: 'РМА' },
  awayTeam: { id: 11, name: 'Барселона', shortName: 'БАР' },
  startsAt: '2030-01-01T19:00:00.000Z',
  predictionsCloseAt: '2030-01-01T19:00:00.000Z',
  isOpenForPredictions: true,
  status: 'OPEN',
  availableTypes: [
    {
      type: 'MATCH_OUTCOME',
      basePoints: 30,
      funcoinCost: 10,
      alreadyPredicted: false,
    },
    {
      type: 'EXACT_SCORE',
      basePoints: 100,
      funcoinCost: 10,
      minScoreValue: 0,
      maxScoreValue: 9,
      alreadyPredicted: false,
    },
    // Оба типа сервер отдаёт со второго Модуля. Держим их на первом
    // демо-матче, чтобы вкладки сразу
    // сложились в грид 2×2 (узел 824:9156), а не в одиночку требовали
    // отдельного матча ради превью.
    {
      type: 'BOTH_TEAMS_TO_SCORE',
      basePoints: 10,
      funcoinCost: 10,
      alreadyPredicted: false,
    },
    {
      type: 'TOTAL_OVER_UNDER',
      basePoints: 10,
      funcoinCost: 10,
      // Порог приходит с сервера; в заглушке — значение из конфига
      totalLine: 2.5,
      alreadyPredicted: false,
    },
  ],
  myPredictions: [],
  // Лидер третий — состояние, нарисованное в ките (438:50968)
  outcomeDistribution: { home: 128, draw: 402, away: 771, total: 1301 },
  // Лидер «Больше» — тот же принцип, что у outcomeDistribution выше
  totalDistribution: { over: 356, under: 214, total: 570 },
};

const MATCH_18: MatchCard = {
  id: '18',
  competition: { id: 1, name: 'Лига чемпионов' },
  homeTeam: { id: 12, name: 'Ливерпуль', shortName: 'ЛИВ' },
  awayTeam: { id: 13, name: 'Милан', shortName: 'МИЛ' },
  startsAt: '2030-01-02T19:00:00.000Z',
  predictionsCloseAt: '2030-01-02T19:00:00.000Z',
  isOpenForPredictions: true,
  status: 'OPEN',
  availableTypes: [
    {
      type: 'MATCH_OUTCOME',
      basePoints: 30,
      funcoinCost: 10,
      alreadyPredicted: false,
    },
  ],
  myPredictions: [],
  // Лидер посередине: обе крайние доли нейтральные, и видно,
  // что они не сливаются
  outcomeDistribution: { home: 96, draw: 271, away: 88, total: 455 },
  totalDistribution: { over: 264, under: 191, total: 455 },
};

const OPEN_MATCHES: MatchCard[] = [MATCH_17, MATCH_18];

/** Тот же матч 17, но с уже поставленным исходом — карточка «В процессе». */
const PROGRESS_MATCHES: MatchCard[] = [
  {
    ...MATCH_17,
    availableTypes: MATCH_17.availableTypes.map((type) => ({
      ...type,
      alreadyPredicted: true,
    })),
    // Сам Прогноз обязан быть рядом с флагом: на бэкенде
    // `alreadyPredicted` ВЫВОДИТСЯ из `myPredictions`
    // (`MatchCardDto.build`), и флаг без Прогноза — состояние,
    // которого в жизни не бывает. Заглушка его допускала, и на ней
    // нельзя было проверить, что форма показывает сделанный выбор.
    myPredictions: [
      {
        id: '917',
        matchId: MATCH_17.id,
        type: 'MATCH_OUTCOME',
        status: 'CONFIRMED',
        outcome: 'HOME',
        pointsAwarded: 0,
        createdAt: '2030-01-01T12:00:00.000Z',
      },
      {
        id: '918',
        matchId: MATCH_17.id,
        type: 'EXACT_SCORE',
        status: 'CONFIRMED',
        homeScoreGuess: 2,
        awayScoreGuess: 1,
        pointsAwarded: 0,
        createdAt: '2030-01-01T12:02:00.000Z',
      },
    ],
  },
  MATCH_18,
  // Матч ИДЁТ, Прогноз поставлен, результата ещё нет. Бэкенд держит такой
  // в «Текущих» (matches.controller.ts) и намеренно не отдаёт по нему счёт
  // и исход. Тап по карточке открывает детали ставки со статусом
  // «в процессе» — состояние заведено 09.09.2026 вместе с этой возможностью.
  {
    id: '21',
    competition: { id: 1, name: 'Лига чемпионов' },
    homeTeam: { id: 16, name: 'Спартак', shortName: 'СПА' },
    awayTeam: { id: 17, name: 'Зенит', shortName: 'ЗЕН' },
    startsAt: '2030-01-01T18:00:00.000Z',
    predictionsCloseAt: '2030-01-01T18:00:00.000Z',
    isOpenForPredictions: false,
    status: 'LIVE',
    availableTypes: [
      {
        type: 'MATCH_OUTCOME',
        basePoints: 30,
        funcoinCost: 10,
        alreadyPredicted: true,
      },
      {
        type: 'EXACT_SCORE',
        basePoints: 100,
        funcoinCost: 30,
        alreadyPredicted: true,
      },
    ],
    // Два типа сразу: игрок часто ставит и исход, и счёт. Экран деталей
    // обязан показать оба выбора, пока матч идёт.
    myPredictions: [
      {
        id: '921',
        matchId: '21',
        type: 'MATCH_OUTCOME',
        status: 'CONFIRMED',
        outcome: 'AWAY',
        pointsAwarded: 0,
        createdAt: '2030-01-01T12:00:00.000Z',
      },
      {
        id: '922',
        matchId: '21',
        type: 'EXACT_SCORE',
        status: 'CONFIRMED',
        homeScoreGuess: 1,
        awayScoreGuess: 3,
        pointsAwarded: 0,
        createdAt: '2030-01-01T12:01:00.000Z',
      },
    ],
    outcomeDistribution: { home: 120, draw: 40, away: 80, total: 240 },
    totalDistribution: { over: 139, under: 101, total: 240 },
  },
];

/** Вкладка «Завершённые» (GET /matches) — по одному примеру на исход. */
const FINISHED_MATCHES: MatchCard[] = [
  {
    id: '19',
    competition: { id: 1, name: 'Лига чемпионов' },
    homeTeam: { id: 14, name: 'Динамо', shortName: 'ДИН' },
    awayTeam: { id: 15, name: 'Локомотив', shortName: 'ЛОК' },
    startsAt: '2030-01-01T20:00:00.000Z',
    predictionsCloseAt: '2030-01-01T20:00:00.000Z',
    isOpenForPredictions: false,
    status: 'FINISHED',
    homeScore: 2,
    awayScore: 1,
    availableTypes: [
      {
        type: 'MATCH_OUTCOME',
        basePoints: 30,
        funcoinCost: 10,
        alreadyPredicted: true,
      },
    ],
    myPredictions: [
      {
        id: '901',
        matchId: '19',
        type: 'MATCH_OUTCOME',
        status: 'WON',
        outcome: 'HOME',
        pointsAwarded: 30,
        createdAt: '2030-01-01T12:00:00.000Z',
      },
    ],
    // Ничья наверху — лидера нет, зелёного в полосе не будет
    outcomeDistribution: { home: 340, draw: 340, away: 61, total: 741 },
    totalDistribution: { over: 430, under: 311, total: 741 },
  },
  {
    id: '20',
    competition: { id: 1, name: 'Лига чемпионов' },
    homeTeam: { id: 16, name: 'ЦСКА', shortName: 'ЦСК' },
    awayTeam: { id: 17, name: 'Ростов', shortName: 'РОС' },
    startsAt: '2030-01-01T22:00:00.000Z',
    predictionsCloseAt: '2030-01-01T22:00:00.000Z',
    isOpenForPredictions: false,
    status: 'FINISHED',
    homeScore: 0,
    awayScore: 0,
    availableTypes: [
      {
        type: 'MATCH_OUTCOME',
        basePoints: 30,
        funcoinCost: 10,
        alreadyPredicted: true,
      },
    ],
    myPredictions: [
      {
        id: '902',
        matchId: '20',
        type: 'MATCH_OUTCOME',
        status: 'LOST',
        outcome: 'HOME',
        pointsAwarded: 0,
        createdAt: '2030-01-01T14:00:00.000Z',
      },
    ],
    // Перекос: у доли в 1% остаётся только её подпись
    outcomeDistribution: { home: 9, draw: 4, away: 812, total: 825 },
    totalDistribution: { over: 478, under: 347, total: 825 },
  },
];

/**
 * Наборы матчей на выбор — переключаются `?scenario=` в адресе, без пересборки.
 */
const SCENARIOS = {
  default: OPEN_MATCHES,
  empty: [] as MatchCard[],
  progress: PROGRESS_MATCHES,
} satisfies Record<string, MatchCard[]>;

function currentMatches(): MatchCard[] {
  const params = new URLSearchParams(window.location.search);
  const scenario = params.get('scenario');
  if (scenario !== null && scenario in SCENARIOS) {
    return SCENARIOS[scenario as keyof typeof SCENARIOS];
  }
  // ?derby-finished=1 без явного ?scenario= — лента тоже пустая: карточка
  // Дерби показывается только в пустой ленте Прогнозов (см. MatchList.tsx).
  return params.has('derby-finished') ? SCENARIOS.empty : SCENARIOS.default;
}

// Факты — отдельно от вопроса: сервер отдаёт их только вместе с ответом
// (см. AnswerDerbyResult.factText), до ответа подсматривать нельзя даже
// в демо-заглушке — иначе вёрстка проверялась бы не по тому контракту.
const DERBY_FACTS: Record<number, string> = {
  11: 'Реал Мадрид выиграл Лигу чемпионов 15 раз — больше, чем любой другой клуб.',
  12: '«Милан» выигрывал турнир 7 раз, «Барселона» — 5.',
};

// Мутируется в /derby/answer — как и MATCHES, состояние держим ради вёрстки
// прохождения (иначе второй вопрос никогда не завершался бы: захардкоженный
// nextPosition делал бы вид, что кнопки не отвечают на клик).
// closesAt — минута от старта демо-сервера: удобно проверять кольцо-таймер
// вживую, не дожидаясь настоящего окна Дерби. currentDeadline — 20 секунд
// от текущего вопроса, тем же приёмом: не успел выбрать — сработает
// onExpire и заглушка (как настоящий сервер) защитает вопрос неверным.
const derbyState: DerbyState = {
  id: 1,
  title: 'Дерби недели',
  opensAt: '2030-01-01T00:00:00.000Z',
  closesAt: new Date(Date.now() + 60_000).toISOString(),
  questions: [
    {
      id: 11,
      position: 1,
      text: 'Кто чаще выигрывал Лигу чемпионов?',
      optionA: 'Реал Мадрид',
      optionB: 'Барселона',
    },
    {
      id: 12,
      position: 2,
      text: 'Милан выигрывал ЛЧ больше пяти раз?',
      optionA: 'Да',
      optionB: 'Нет',
    },
  ],
  currentPosition: 1,
  currentDeadline: new Date(Date.now() + 20_000).toISOString(),
  correctCount: 0,
  isFinished: false,
  // Карточка Дерби на пустой ленте называет прибавку за ОДИН ответ,
  // а не награду за всё прохождение (`maxPoints`) — в демо-режиме
  // подпись должна выглядеть так же, как в игре
  maxPointsPerAnswer: 20,
  // Демо не выбрасывает приглашение само: панель смотрят по ?derby-nudge=1
  shouldNudge: false,
};

/**
 * Ответ как у сервера: формулировка видна только у ОТВЕЧЕННОГО
 * и у ОТКРЫТОГО вопроса. Открытый — тот, чей отсчёт идёт.
 *
 * Иначе демо показывало бы все пять вопросов сразу — поведение, которого
 * в игре нет: читать вперёд нельзя, иначе таймер ничего не ограничивает.
 */
function visibleDerby(): DerbyState {
  const openPosition =
    derbyState.currentDeadline === undefined
      ? null
      : (derbyState.currentPosition ?? null);
  return {
    ...derbyState,
    // Время на вопрос — парой с дедлайном, как у сервера: кольцу-таймеру
    // нужна длина отсчёта. Все дедлайны заглушки ставятся на 20 секунд
    ...(openPosition === null ? {} : { currentTimeLimitSeconds: 20 }),
    questions: derbyState.questions.map((q) => {
      if (q.isCorrect !== undefined || q.position === openPosition) return q;
      const { text: _t, optionA: _a, optionB: _b, ...hidden } = q;
      return hidden;
    }),
  };
}

/** Идемпотентно: повторный вызов после того же дедлайна ничего не меняет. */
function timeoutCurrentDerbyQuestion(): void {
  if (derbyState.isFinished || !derbyState.currentDeadline) return;
  if (Date.now() < new Date(derbyState.currentDeadline).getTime()) return;

  const current = derbyState.questions.find(
    (q) => q.position === derbyState.currentPosition,
  );
  if (!current) return;

  const isLast = current.position === derbyState.questions.length;
  const fact = DERBY_FACTS[current.id];
  derbyState.questions = derbyState.questions.map((q) =>
    q.id === current.id
      ? {
          ...q,
          isCorrect: false,
          ...(fact === undefined ? {} : { factText: fact }),
        }
      : q,
  );
  derbyState.isFinished = isLast;
  derbyState.currentPosition = isLast ? current.position : current.position + 1;
  if (isLast) {
    delete derbyState.currentDeadline;
  } else {
    derbyState.currentDeadline = new Date(Date.now() + 20_000).toISOString();
  }
}

/** GET /reward-track — Глава 1, один пройденный и один предстоящий Энкаунтер. */
const REWARD_TRACK: RewardTrack = {
  chapterIndex: 1,
  chapterTitle: 'Глава 1',
  points: 100,
  claimed: [
    {
      id: 1,
      position: 1,
      pointsThreshold: 100,
      rewardType: 'FUNCOINS',
      rewardValue: '200',
      title: '200 фанкоинов',
      iconUrl: '/images/career/reward-biceps.png',
    },
  ],
  upcoming: [
    {
      id: 2,
      position: 2,
      pointsThreshold: 200,
      rewardType: 'FUNCOINS',
      rewardValue: '300',
      title: '300 фанкоинов',
      iconUrl: '/images/career/reward-biceps.png',
    },
  ],
  next: {
    id: 2,
    position: 2,
    pointsThreshold: 200,
    rewardType: 'FUNCOINS',
    rewardValue: '300',
    title: '300 фанкоинов',
    description:
      'Собери 200 очков и получи 300 фанкоинов, чтобы делать больше прогнозов',
    iconUrl: '/images/career/reward-biceps.png',
  },
  pointsToNext: 100,
  chapterStartsAt: '2026-09-07T21:00:00.000Z',
  chapterEndsAt: '2026-10-07T21:00:00.000Z',
  nextChapterStartsAt: '2026-10-07T21:00:00.000Z',
};

// Список длиннее экрана нарочно — короче него закреплённая плашка «я»
// наезжает на последнюю строку вместо того, чтобы висеть под ней.
const WEEKLY_ENTRIES = [
  ...Array.from({ length: 15 }, (_, i) => ({
    userId: String(i + 1),
    rank: i + 1,
    points: 9000 - i * 400,
    displayName: `@player_${String(i + 1)}`,
  })),
  { userId: '42', rank: 42, points: 500, displayName: '@демо-игрок' },
];

/** GET /leaderboard/weekly — для «Розыгрышей» и «Рейтинга». */
const WEEKLY_LEADERBOARD: Leaderboard = {
  entries: WEEKLY_ENTRIES,
  me: { rank: 42, points: 500, totalPlayers: 100, inPrizeZone: false },
  totalPlayers: 100,
  prizeTiers: [
    { rankFrom: 1, rankTo: 5, pointsReward: 1000, freebetReward: 1000 },
    { rankFrom: 6, rankTo: 15, pointsReward: 750, freebetReward: 500 },
    { rankFrom: 16, rankTo: 50, pointsReward: 500, freebetReward: 0 },
    { rankFrom: 51, rankTo: 100, pointsReward: 250, freebetReward: 0 },
  ],
  pools: [],
};

// Место 7 занято «мной» — демонстрирует розовую строку в топ-10 (state
// you-top), а не только закреплённую плашку.
const MONTHLY_ENTRIES = Array.from({ length: 15 }, (_, i) => ({
  userId: String(i + 1),
  rank: i + 1,
  points: i === 6 ? 4200 : 30_000 - i * 1800,
  displayName: i === 6 ? '@демо-игрок' : `@player_${String(i + 1)}`,
}));

/** GET /leaderboard/monthly — «Рейтинг», вкладка «Месячный». */
const MONTHLY_LEADERBOARD: Leaderboard = {
  entries: MONTHLY_ENTRIES,
  me: { rank: 7, points: 4200, totalPlayers: 500, inPrizeZone: true },
  totalPlayers: 500,
  prizeTiers: [],
  pools: [
    { type: 'TOP_10', rankFrom: 1, rankTo: 10, winnersCount: 10 },
    { type: 'TOP_100', rankFrom: 1, rankTo: 100, winnersCount: 100 },
    { type: 'TOP_1000', rankFrom: 1, rankTo: 1000, winnersCount: 1000 },
  ],
};

const BALANCE: FuncoinBalance = { balance: 180 };
/**
 * Итоги дня. `?popup=1` в адресе — посмотреть модалку успеха без правки
 * файла; по умолчанию её нет, иначе она закрывала бы каждый экран.
 */
const POPUP: PredictionPopup = {
  hasResults: new URLSearchParams(window.location.search).has('popup'),
  scenario: 'SUCCESS',
  title: 'Твои прогнозы зашли',
  body: 'А ты хорош, получай очки:',
  totalPoints: 1250,
  predictions: [],
};

/**
 * Отдаёт заготовленный ответ или `undefined`, если путь не покрыт.
 *
 * Мутации отвечают правдоподобно, но состояние не держат: цель — вёрстка,
 * а не воспроизведение игровой логики. Сценарии со сменой состояния
 * проверяются e2e-фикстурами, у которых состояние есть.
 */
export function demoResponder(path: string, options: RequestOptions): unknown {
  switch (path) {
    case '/auth/telegram':
      return {
        accessToken: 'demo.jwt.token',
        expiresIn: '7d',
        // ?welcome=1 — проверка Welcome-модалки без правки этого файла.
        isNewUser: new URLSearchParams(window.location.search).has('welcome'),
      } satisfies AuthResponse;

    case '/users/me':
      // ?subscribe=1 — проверка SubscribeModal без правки этого файла.
      return new URLSearchParams(window.location.search).has('subscribe')
        ? { ...USER, isSubscribed: false }
        : USER;

    // Побочного действия в демо нет: статус выдуман и не меняется.
    // `?subscribe=1` держит игрока неподписанным — перепроверка тоже
    // обязана отвечать «нет», иначе сценарий сам себя закроет
    case '/users/me/subscription/refresh':
      return {
        isSubscribed: !new URLSearchParams(window.location.search).has(
          'subscribe',
        ),
        checkedAt: new Date().toISOString(),
      };

    case '/users/me/summary':
      return new URLSearchParams(window.location.search).has('subscribe')
        ? { ...USER_SUMMARY, profile: { ...USER, isSubscribed: false } }
        : USER_SUMMARY;

    case '/users/me/display-name': {
      const sent = (options.body as { displayName?: string } | undefined)
        ?.displayName;
      const profile: UserProfile = {
        ...USER,
        ...(sent === undefined ? {} : { displayName: sent }),
      };
      return profile;
    }

    case '/matches/open':
      return currentMatches();

    case '/matches/finished':
      return FINISHED_MATCHES;

    case '/predictions/mine':
      return [] as Prediction[];

    case '/predictions/funcoins':
      return BALANCE;

    case '/predictions/popup':
      return POPUP;

    case '/predictions/popup/seen':
      return { ok: true };

    case '/predictions': {
      // Эхо реального выбора: applyPrediction (matches.store.ts) помечает
      // alreadyPredicted по `prediction.type` из ответа, не из заготовки —
      // захардкоженный МATCH_OUTCOME/HOME ломал бы Голы и Первый гол.
      const body = options.body as CreatePredictionPayload;
      const prediction: Prediction = {
        id: String(Date.now()),
        matchId: body.matchId,
        type: body.type,
        status: 'CONFIRMED',
        pointsAwarded: 0,
        createdAt: new Date().toISOString(),
        ...(body.outcome === undefined ? {} : { outcome: body.outcome }),
        ...(body.firstGoal === undefined ? {} : { firstGoal: body.firstGoal }),
        ...(body.homeScoreGuess === undefined
          ? {}
          : { homeScoreGuess: body.homeScoreGuess }),
        ...(body.awayScoreGuess === undefined
          ? {}
          : { awayScoreGuess: body.awayScoreGuess }),
      };
      return { prediction, funcoinBalance: BALANCE.balance - 10 };
    }

    case '/achievements':
      return ACHIEVEMENTS;

    case '/freebets':
      return FREEBETS;

    case '/freebets/claim':
      return CLAIM;

    case '/draws/current':
      return CURRENT_DRAWS;

    case '/draws/monthly':
      return MONTHLY_DRAW;

    case '/draws/season-final':
      return SEASON_FINAL;

    case '/community/daily-activity':
      return DAILY_ACTIVITY;

    case '/results/pending':
      // ?period-result=1 — посмотреть модалку итога недели без правки файла
      return {
        hasResult: new URLSearchParams(window.location.search).has(
          'period-result',
        ),
        result: {
          kind: 'WEEKLY_TOURNAMENT',
          refId: 7,
          settledAt: '2026-09-14T00:00:00.000Z',
          periodStartsAt: '2026-09-08T00:00:00.000Z',
          nextPeriodStartsAt: '2026-09-14T00:00:00.000Z',
          rank: 4,
          points: 4200,
          pointsAwarded: 300,
          freebetsAwarded: 0,
          freebetsForfeited: 0,
          isWinner: false,
          prizeTitle: null,
          prizeKind: null,
        },
      } satisfies PendingResult;

    case '/results/seen':
      return { seen: true };

    case '/reward-track':
      return REWARD_TRACK;

    case '/leaderboard/weekly':
      return WEEKLY_LEADERBOARD;

    case '/leaderboard/monthly':
      return MONTHLY_LEADERBOARD;

    // Старт отвечает тем же, что и чтение: побочное действие живёт
    // на сервере, а в демо-режиме дедлайны и так приходят из заглушки
    case '/derby/start':
    case '/derby/current':
      // ?derby-finished=1 — проверка карточки/модалки «Дерби пройдено»
      // без ручного прохождения квиза.
      if (new URLSearchParams(window.location.search).has('derby-finished')) {
        return {
          ...derbyState,
          isFinished: true,
          correctCount: 1,
          pointsAwarded: 20,
        };
      }
      // Дедлайн текущего вопроса истёк, а игрок не ответил — сервер сам
      // защитывает вопрос неверным; клиент узнаёт об этом, перечитывая
      // состояние (см. onExpire в use-countdown), а не решая это сам.
      timeoutCurrentDerbyQuestion();
      return visibleDerby();

    // Кнопка «Следующий»: здесь и начинается отсчёт вопроса
    case '/derby/next':
      if (!derbyState.isFinished) {
        derbyState.currentDeadline = new Date(
          Date.now() + 20_000,
        ).toISOString();
      }
      return visibleDerby();

    case '/derby/answer': {
      // Правильный всегда «A» — как в e2e-фикстуре, реального знания
      // футбола тут не проверяем.
      const body = options.body as { questionId: number; option: 'A' | 'B' };
      const isCorrect = body.option === 'A';
      const question = derbyState.questions.find(
        (q) => q.id === body.questionId,
      );
      const position = question?.position ?? derbyState.currentPosition ?? 1;
      const isLast = position === derbyState.questions.length;
      const factText = DERBY_FACTS[body.questionId];

      derbyState.questions = derbyState.questions.map((q) =>
        q.id === body.questionId
          ? {
              ...q,
              chosenOption: body.option,
              isCorrect,
              ...(factText === undefined ? {} : { factText }),
            }
          : q,
      );
      derbyState.correctCount += isCorrect ? 1 : 0;
      derbyState.isFinished = isLast;
      // Награда живёт и в состоянии: экран итога читает её оттуда
      if (isLast) derbyState.pointsAwarded = derbyState.correctCount * 20;
      // У пройденного Дерби текущего вопроса нет — так отдаёт сервер
      if (isLast) delete derbyState.currentPosition;
      else derbyState.currentPosition = position + 1;
      // Таймер следующего вопроса ЕЩЁ НЕ ИДЁТ: его запускает `/derby/next`,
      // то есть кнопка «Следующий». Пока игрок читает факт, время стоит
      delete derbyState.currentDeadline;

      return {
        isCorrect,
        correctOption: 'A',
        ...(factText === undefined ? {} : { factText }),
        isFinished: isLast,
        correctCount: derbyState.correctCount,
        pointsSoFar: derbyState.correctCount * 20,
        ...(isLast
          ? { pointsAwarded: derbyState.correctCount * 20 }
          : { nextPosition: position + 1 }),
      };
    }

    default:
      // Не «пустой ответ», а «иди в сеть»: неизвестный путь должен
      // упасть понятной ошибкой, а не притвориться пустым экраном
      return undefined;
  }
}
